import React, { useState, useEffect, useCallback } from 'react';
import { behavioralAPI, sourcingAPI } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Target, Users, Sparkles, ChevronDown, ChevronUp, AlertTriangle, Search } from 'lucide-react';
import { BarGraph, TRAIT_COLORS, TRAIT_NAMES } from './DiscGraphs';

const TERRA = '#0076A3';
const TRAITS = ['D', 'I', 'S', 'C'];
const bandColor = (b) => (b === 'Strong' ? '#059669' : b === 'Moderate' ? '#D97706' : '#DC2626');

function TargetSetter({ jobId, archetypes, initial, initialArchetype, onSaved }) {
  const [target, setTarget] = useState(initial || { D: 50, I: 50, S: 50, C: 50 });
  const [archetype, setArchetype] = useState(initialArchetype || '');
  const [saving, setSaving] = useState(false);
  const [basedOn, setBasedOn] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const pickArchetype = (id) => {
    setArchetype(id);
    setBasedOn(null);
    const a = archetypes.find((x) => x.id === id);
    if (a) setTarget({ ...a.target });
  };

  const buildFromTop = async () => {
    setSuggesting(true);
    try {
      const { data } = await behavioralAPI.suggestTarget(jobId);
      const rounded = {};
      ['D', 'I', 'S', 'C'].forEach((t) => { rounded[t] = Math.round(data.suggested_target[t] || 0); });
      setTarget(rounded);
      setArchetype('');
      setBasedOn(data);
      toast.success(`Built from ${data.count} top performer${data.count !== 1 ? 's' : ''} — review & save`);
    } catch (e) {
      toast.info(e.response?.data?.detail || 'Not enough profile data to suggest a target yet');
    } finally { setSuggesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await behavioralAPI.setJobTarget(jobId, { target, archetype_id: archetype || null });
      toast.success('Ideal profile saved');
      onSaved();
    } catch (e) { toast.error('Could not save target'); } finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await behavioralAPI.setJobTarget(jobId, { target: null });
      toast.success('Ideal profile cleared');
      onSaved();
    } catch (e) { toast.error('Could not clear target'); } finally { setSaving(false); }
  };

  return (
    <div data-testid="behavioral-target-setter">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <label style={{ fontSize: '0.8rem', color: '#57534E', fontWeight: 600 }}>Start from archetype:</label>
        <select data-testid="behavioral-archetype-select" value={archetype} onChange={(e) => pickArchetype(e.target.value)}
          style={{ border: '1px solid #E7E5E4', borderRadius: 8, padding: '0.45rem 0.6rem', fontSize: '0.83rem', background: '#fff', minWidth: 220 }}>
          <option value="">Custom…</option>
          {archetypes.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <span style={{ fontSize: '0.78rem', color: '#A8A29E' }}>or</span>
        <Button data-testid="behavioral-build-from-top" variant="outline" size="sm" onClick={buildFromTop} disabled={suggesting}
          style={{ borderColor: TERRA, color: TERRA }}>
          <Sparkles size={14} style={{ marginRight: 6 }} /> {suggesting ? 'Building…' : 'Build from top performers'}
        </Button>
        {archetype && <span style={{ fontSize: '0.75rem', color: '#78716C', fontStyle: 'italic', flexBasis: '100%' }}>{archetypes.find((a) => a.id === archetype)?.blurb}</span>}
      </div>
      {basedOn && (
        <div data-testid="behavioral-suggest-basis" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '0.7rem 0.9rem', marginBottom: 14, fontSize: '0.78rem', color: '#047857' }}>
          Built from <strong>{basedOn.count}</strong> {basedOn.basis === 'hired' ? 'candidate(s) at offer/hired stage' : 'top-scoring candidate(s)'}: {basedOn.based_on.map((b) => b.name).join(', ')}. Review the sliders below, then save.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        {TRAITS.map((t) => (
          <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 130, fontSize: '0.8rem', color: '#44403C' }}>
              <span style={{ fontWeight: 700, color: TRAIT_COLORS[t] }}>{t}</span> {TRAIT_NAMES[t]}
            </div>
            <input type="range" min="0" max="100" value={target[t]} data-testid={`behavioral-target-slider-${t}`}
              onChange={(e) => { setTarget({ ...target, [t]: Number(e.target.value) }); setArchetype(''); setBasedOn(null); }}
              style={{ flex: 1, accentColor: TRAIT_COLORS[t] }} />
            <div style={{ width: 34, textAlign: 'right', fontWeight: 700, fontSize: '0.82rem', color: '#002A38' }}>{target[t]}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button data-testid="behavioral-save-target" onClick={save} disabled={saving} style={{ background: TERRA, color: '#fff' }}>{saving ? 'Saving…' : 'Save ideal profile'}</Button>
        {initial && <Button data-testid="behavioral-clear-target" onClick={clear} disabled={saving} variant="outline">Clear</Button>}
      </div>
    </div>
  );
}

function BlendChips({ blend }) {
  const chip = (label, items, col) => items.length > 0 && (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 8, marginBottom: 4 }}>
      <span style={{ fontSize: '0.64rem', color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}:</span>
      {items.map((t) => <span key={t} style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: `${col}18`, color: col }}>{TRAIT_NAMES[t]}</span>)}
    </span>
  );
  return (
    <div style={{ marginTop: 8 }}>
      <p style={{ fontSize: '0.8rem', color: '#57534E', lineHeight: 1.5, margin: '0 0 6px' }}>{blend.summary}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
        {chip('Complements', blend.complements || [], '#7FB08A')}
        {chip('Reinforces', blend.reinforces || [], '#7E8FB5')}
      </div>
      {(blend.frictions || []).map((f) => (
        <div key={f.text} style={{ display: 'flex', gap: 6, marginTop: 6, fontSize: '0.76rem', color: '#B45309', lineHeight: 1.45 }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} /> <span>{f.text}</span>
        </div>
      ))}
    </div>
  );
}

export default function BehavioralFitPanel({ jobId }) {
  const navigate = useNavigate();
  const [fit, setFit] = useState(null);
  const [archetypes, setArchetypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [f, a] = await Promise.all([behavioralAPI.jobFit(jobId), behavioralAPI.archetypes()]);
      setFit(f.data);
      setArchetypes(a.data.archetypes || []);
    } catch (e) { /* non-fatal */ } finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const openTraitSearch = async (trait) => {
    setSearchOpen(true); setSearching(true); setSearchData(null);
    try {
      const { data } = await behavioralAPI.traitSearch(trait, jobId);
      setSearchData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run the search');
      setSearchOpen(false);
    } finally { setSearching(false); }
  };

  const addToRole = async (row) => {
    setAddingId(row.session_id);
    try {
      await sourcingAPI.addSourced(jobId, { candidate_id: row.candidate_id, overall_score: row.overall_score });
      toast.success(`${row.name} added to this role`);
      setSearchData((prev) => ({ ...prev, candidates: prev.candidates.map((c) => c.session_id === row.session_id ? { ...c, _added: true } : c) }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add candidate');
    } finally { setAddingId(null); }
  };

  if (loading) return <Card style={{ padding: '1.5rem', color: '#78716C' }}>Loading psychometric fit…</Card>;
  if (!fit) return null;

  const hasTarget = !!fit.behavioral_target;
  const members = fit.members || [];
  const team = fit.team || { size: 0, avg: {} };

  return (
    <div data-testid="behavioral-fit-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Role Fit */}
      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.05rem', fontWeight: 700, color: '#002A38', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Target size={18} color={TERRA} /> Role Fit
          </h3>
          <Button data-testid="behavioral-edit-target" variant="outline" onClick={() => setEditing((v) => !v)} size="sm">
            {editing ? 'Close' : hasTarget ? 'Edit ideal profile' : 'Set ideal profile'}
          </Button>
        </div>

        {editing && (
          <div style={{ background: '#FAF7F2', borderRadius: 12, padding: '1.25rem', marginBottom: 16 }}>
            <TargetSetter jobId={jobId} archetypes={archetypes} initial={fit.behavioral_target}
              initialArchetype={fit.behavioral_archetype} onSaved={() => { setEditing(false); load(); }} />
          </div>
        )}

        {!hasTarget && !editing && (
          <p data-testid="behavioral-no-target" style={{ fontSize: '0.85rem', color: '#78716C' }}>
            Set an ideal DISC profile for this role to score how well each candidate matches.
          </p>
        )}

        {hasTarget && (
          members.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: '#78716C' }}>No completed psychometric profiles for this role yet. Invite candidates from their profile page.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {members.map((m) => (
                <div key={m.session_id} data-testid={`behavioral-fit-row-${m.session_id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0.75rem 0.9rem', border: '1px solid #F1EDE8', borderRadius: 10 }}>
                  <div style={{ textAlign: 'center', width: 46, flexShrink: 0 }}>
                    <div style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 800, fontSize: '1.35rem', color: bandColor(m.fit?.band), lineHeight: 1 }}>{m.fit ? Math.round(m.fit.score) : '—'}</div>
                    <div style={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', color: bandColor(m.fit?.band) }}>{m.fit?.band}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#002A38', fontSize: '0.88rem' }}>{m.candidate_name} <span style={{ color: '#A8A29E', fontWeight: 500 }}>· {m.style_code}</span></div>
                    <div style={{ fontSize: '0.75rem', color: '#78716C' }}>{m.fit?.summary?.replace(/^(Strong|Moderate|Weak) fit\.\s*/, '') || ''}</div>
                  </div>
                  {m.candidate_id && (
                    <a href={`/candidate/${m.candidate_id}`} style={{ fontSize: '0.72rem', color: TERRA, fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>Open →</a>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </Card>

      {/* Team Chemistry */}
      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.05rem', fontWeight: 700, color: '#002A38', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <Users size={18} color={TERRA} /> Team Chemistry
          <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#A8A29E' }}>· {team.size} profiled</span>
        </h3>
        {fit.gap_suggestion && team.size > 0 && (() => {
          const gs = fit.gap_suggestion;
          const actionable = gs.trait && gs.covers;
          const col = gs.trait ? (gs.covers ? TERRA : '#D97706') : '#059669';
          const bg = gs.trait ? (gs.covers ? '#FFF3EC' : '#FFFBEB') : '#F0FDF4';
          return (
            <div data-testid="behavioral-gap-suggestion" style={{ background: bg, border: `1px solid ${col}33`, borderRadius: 12, padding: '0.85rem 1rem', marginBottom: 14, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <Sparkles size={16} color={col} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: col, marginBottom: 2 }}>Team gap suggestion</div>
                <div style={{ fontSize: '0.85rem', color: '#44403C', lineHeight: 1.5 }}>{gs.message}</div>
                {gs.trait && (
                  <button data-testid="behavioral-find-trait-btn" onClick={() => openTraitSearch(gs.trait)}
                    style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', fontWeight: 700, color: col, background: '#fff', border: `1px solid ${col}55`, borderRadius: 999, padding: '0.35rem 0.8rem', cursor: 'pointer' }}>
                    <Search size={13} /> Find candidates strong in {gs.trait_name} →
                  </button>
                )}
              </div>
              {actionable && gs.candidate?.candidate_id && (
                <a href={`/candidate/${gs.candidate.candidate_id}`} data-testid="behavioral-gap-candidate-link" style={{ fontSize: '0.75rem', color: col, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>View →</a>
              )}
            </div>
          );
        })()}
        {team.size === 0 ? (
          <p style={{ fontSize: '0.85rem', color: '#78716C' }}>No completed profiles yet — the team map appears once candidates finish their assessment.</p>
        ) : (
          <div data-testid="behavioral-team-chemistry" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)', gap: '1.5rem' }} className="team-chem-grid">
            <div>
              <BarGraph title="Team balance" subtitle={`Leans ${team.dominant_name || ''}`} data={team.avg} testid="behavioral-team-avg" />
              {(team.gaps || []).length > 0 && (
                <div style={{ fontSize: '0.76rem', color: '#B45309', marginTop: 4, display: 'flex', gap: 6 }}>
                  <Sparkles size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>Under-represented: {team.gaps.map((t) => TRAIT_NAMES[t]).join(', ')} — a candidate strong here would balance the team.</span>
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#78716C', marginBottom: 8 }}>How each member fits the mix</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {members.map((m) => {
                  const open = expanded === m.session_id;
                  return (
                    <div key={m.session_id} style={{ border: '1px solid #F1EDE8', borderRadius: 10, overflow: 'hidden' }}>
                      <button data-testid={`behavioral-blend-toggle-${m.session_id}`} onClick={() => setExpanded(open ? null : m.session_id)}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.8rem', background: open ? '#FAF7F2' : '#fff', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 9, height: 9, borderRadius: 3, background: TRAIT_COLORS[m.primary_style] }} />
                          <span style={{ fontWeight: 600, fontSize: '0.84rem', color: '#002A38' }}>{m.candidate_name}</span>
                          <span style={{ fontSize: '0.72rem', color: '#A8A29E' }}>{m.style_code}</span>
                        </span>
                        {open ? <ChevronUp size={15} color="#78716C" /> : <ChevronDown size={15} color="#78716C" />}
                      </button>
                      {open && <div style={{ padding: '0 0.8rem 0.8rem' }}><BlendChips blend={m.blend} /></div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent data-testid="behavioral-trait-search-dialog" className="sm:max-w-[620px]" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle>{searchData ? `Candidates strong in ${searchData.trait_name}` : 'Searching your talent pool…'}</DialogTitle>
          </DialogHeader>
          {searching ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#78716C' }}>Searching…</div>
          ) : searchData && searchData.candidates.length === 0 ? (
            <div data-testid="behavioral-trait-search-empty" style={{ padding: '1.5rem', textAlign: 'center' }}>
              <p style={{ color: '#57534E', fontSize: '0.88rem', lineHeight: 1.6 }}>
                No one in your talent pool has proven strongly <strong>{searchData.trait_name}</strong> yet (via a completed psychometric assessment).
              </p>
              <p style={{ color: '#78716C', fontSize: '0.82rem', marginTop: 8 }}>
                Invite more candidates to the assessment, or use the AI Sourcing Kit for external outreach.
              </p>
              <Button onClick={() => navigate(`/sourcing?job=${jobId}`)} variant="outline" style={{ marginTop: 14, borderColor: TERRA, color: TERRA }}>
                Open Sourcing Kit →
              </Button>
            </div>
          ) : searchData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ fontSize: '0.8rem', color: '#78716C', margin: 0 }}>
                {searchData.count} candidate{searchData.count !== 1 ? 's' : ''} in your talent pool proved strong in {searchData.trait_name} and aren't on this role yet.
              </p>
              {searchData.candidates.map((c) => (
                <div key={c.session_id} data-testid={`trait-search-row-${c.session_id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0.7rem 0.85rem', border: '1px solid #F1EDE8', borderRadius: 10 }}>
                  <div style={{ textAlign: 'center', width: 44, flexShrink: 0 }}>
                    <div style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 800, fontSize: '1.2rem', color: TRAIT_COLORS[searchData.trait], lineHeight: 1 }}>{Math.round(c.trait_score)}</div>
                    <div style={{ fontSize: '0.56rem', fontWeight: 700, color: '#A8A29E', textTransform: 'uppercase' }}>{searchData.trait} score</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#002A38', fontSize: '0.88rem' }}>{c.name} <span style={{ color: '#A8A29E', fontWeight: 500 }}>· {c.style_code}</span></div>
                    <div style={{ fontSize: '0.74rem', color: '#78716C' }}>{c.current_job_title ? `Currently: ${c.current_job_title}` : c.email}{c.overall_score != null ? ` · skills ${c.overall_score}` : ''}</div>
                  </div>
                  {c.candidate_id && (
                    <button onClick={() => navigate(`/candidate/${c.candidate_id}`)} style={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>View</button>
                  )}
                  <Button data-testid={`trait-search-add-${c.session_id}`} size="sm" disabled={!c.addable || c._added || addingId === c.session_id}
                    onClick={() => addToRole(c)} style={c._added ? {} : { background: TERRA, color: '#fff' }} variant={c._added ? 'outline' : 'default'}>
                    {c._added ? 'Added ✓' : addingId === c.session_id ? 'Adding…' : 'Add to role'}
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSearchOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`@media (max-width: 820px){ .team-chem-grid{ grid-template-columns: minmax(0,1fr) !important; } }`}</style>
    </div>
  );
}
